/**
 * Message Actions Component - Claude Desktop Style
 *
 * Action buttons that appear below assistant messages:
 * - Copy to clipboard
 * - Thumbs up (always shown)
 * - Thumbs down (always shown)
 * - Retry (regenerate response)
 */

import * as React from 'react'
import { Copy, Check, RotateCcw, ThumbsUp, ThumbsDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface MessageActionsProps {
  content: string
  onRetry?: () => void
  onRate?: (rating: 'up' | 'down') => void
  showRetry?: boolean
  className?: string
}

export function MessageActions({
  content,
  onRetry,
  onRate,
  showRetry = true,
  className,
}: MessageActionsProps) {
  const [copied, setCopied] = React.useState(false)
  const [rating, setRating] = React.useState<'up' | 'down' | null>(null)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleRate = (value: 'up' | 'down') => {
    // Toggle off if same value clicked
    if (rating === value) {
      setRating(null)
      return
    }
    setRating(value)
    onRate?.(value)
  }

  return (
    <div className={cn('flex items-center gap-0.5 -ml-1', className)}>
      {/* Copy */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
        onClick={handleCopy}
        title="Copy to clipboard"
      >
        {copied ? (
          <Check className="h-4 w-4 text-emerald-500" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </Button>

      {/* Thumbs Up - always shown */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg',
          rating === 'up' && 'text-emerald-500 hover:text-emerald-500 bg-emerald-500/10'
        )}
        onClick={() => handleRate('up')}
        title="Good response"
      >
        <ThumbsUp className="h-4 w-4" />
      </Button>

      {/* Thumbs Down - always shown */}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg',
          rating === 'down' && 'text-red-500 hover:text-red-500 bg-red-500/10'
        )}
        onClick={() => handleRate('down')}
        title="Bad response"
      >
        <ThumbsDown className="h-4 w-4" />
      </Button>

      {/* Retry */}
      {showRetry && onRetry && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-foreground rounded-lg"
          onClick={onRetry}
          title="Regenerate response"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
